import React, { useState, useRef, useEffect } from 'react';
import { ChevronRight, FileText } from 'lucide-react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import Sidebar from '../components/layout/Sidebar';
import AskSwirlEmptyState from '../components/chat/AskSwirlEmptyState';
import aiChatService from '../services/aiChatService';
import chatService from '../services/chatService';
import contentService from '../services/contentService';
import topicService from '../services/topicService';
import folderAnalysisService from '../services/folderAnalysisService';
import focusService from '../services/focusService';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import ChatInputBar from '../components/chat/ChatInputBar';
import EnhancedChatMessage from '../components/chat/EnhancedChatMessage';
import PlanMessage from '../components/chat/PlanMessage';
import UploadModal from '../components/chat/UploadModal';
import SwirlContextSelector from '../components/chat/SwirlContextSelector';
import ContentDetailModal from '../components/modals/ContentDetailModal';
import ChatSearchModal from '../components/chat/ChatSearchModal';
import { askSwirl } from '../api/askSwirl';
import { useAskSwirlStore } from '../stores/askSwirlStore';
import deepsearchService from '../services/deepsearchService';
import { supabase } from '../lib/supabase';
import { getSamplePlanConversation } from '../data/samplePlanData';
import DocumentsSidebar from '../components/documents/DocumentsSidebar';
import documentService from '../services/documentService';
import { isDocReady } from '../utils/docReadiness';
import ReferenceCards from '../components/chat/ReferenceCards';
import ReferencePreviewModal from '../components/modals/ReferencePreviewModal';
import ChooseDocModal from '../components/modals/ChooseDocModal';
import { extractPlainTextFromTipTap } from '../utils/tiptapHelpers';

// Dev flag for doc readiness logging
const DEV_DOC_READINESS_LOG = false;

/**
 * AIChatPageReal - Main chat interface with doc message support
 * 
 * DOC MESSAGE FEATURE:
 * - When a user asks to create a doc, the AI can respond with a structured doc document
 * - Docs are rendered as interactive cards with sections, steps, and action buttons
 * - Users can save docs to Focus, save to Swirl, or duplicate in a new chat
 * 
 * TESTING THE DOC FEATURE:
 * - To test with sample data, uncomment the line in handleNewChat that loads sample conversation
 * - Or manually add getSamplePlanConversation() to messages state
 * - In production, the AI backend will return doc-type messages when appropriate
 */
export default function AIChatPageReal() {
  const { user } = useAuth();
  const { isExpanded, toggle } = useSidebar();
  const { clearChips, chips } = useAskSwirlStore();
  const [searchParams] = useSearchParams();
  const { conversationId: urlConversationId } = useParams();
  const navigate = useNavigate();
  const contentId = searchParams.get('content');
  const prefillPrompt = searchParams.get('prompt');
  const focusId = searchParams.get('focusId');
  const itemIds = searchParams.get('itemIds');
  const [selectedSources, setSelectedSources] = useState(['All']);
  const [contextContent, setContextContent] = useState([]);
  const [messages, setMessages] = useState([]);
  // Per-conversation loading state: Map<conversationId, boolean>
  const [loadingStates, setLoadingStates] = useState(new Map());
  const [error, setError] = useState(null);
  const [initialPromptSent, setInitialPromptSent] = useState(false);
  
  // Smart Insights context state (minimal)
  const [contextFocus, setContextFocus] = useState(null);
  const [contextItemsCount, setContextItemsCount] = useState(0);
  
  // Chat management state - MUST be declared before isLoading helper
  const [currentChatId, setCurrentChatId] = useState(null);
  
  // Helper to get loading state for current conversation
  const isLoading = currentChatId ? (loadingStates.get(currentChatId) || false) : false;
  
  // Helper to set loading state for a specific conversation
  const setIsLoading = (conversationId, value) => {
    setLoadingStates(prev => {
      const next = new Map(prev);
      if (value) {
        next.set(conversationId, true);
      } else {
        next.delete(conversationId);
      }
      return next;
    });
  };
  const messagesEndRef = useRef(null);
  const [chats, setChats] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pinnedMoments, setPinnedMoments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [editingChatId, setEditingChatId] = useState(null);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [titleGenerated, setTitleGenerated] = useState(false); // Track if AI title has been generated
  const titleGeneratedRef = useRef(false); // Ref to track title generation in callbacks
  
  // Folder expansion state
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  
  // Documents sidebar state
  const [showDocsSidebar, setShowDocsSidebar] = useState(false);
  const [initialDocumentId, setInitialDocumentId] = useState(null);
  const [isCreatingDoc, setIsCreatingDoc] = useState(false);
  // Support both new and legacy query params for backwards compatibility
  const createDocFromFocusId = searchParams.get('createDocFromFocusId') || searchParams.get('createPlanFromFocusId');
  const openDocumentId = searchParams.get('openDocumentId');
  
  // Reference cards modal state
  const [previewSource, setPreviewSource] = useState(null);
  const [showChooseDocModal, setShowChooseDocModal] = useState(false);
  const [pendingAddContent, setPendingAddContent] = useState(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load user's chats, folders, and pinned moments
  useEffect(() => {
    const loadChatData = async () => {
      if (!user) return;

      try {
        // Load chats and folders first (critical data)
        const [chatsResult, foldersResult] = await Promise.all([
          chatService.getUserChats(user.id),
          chatService.getUserFolders(user.id)
        ]);

        if (chatsResult.data) {
          console.log(`✅ Loaded ${chatsResult.data.length} chats from database`);
          setChats(chatsResult.data);
        } else {
          console.warn('⚠️ No chats data returned from getUserChats');
        }
        if (foldersResult.data) {
          console.log(`✅ Loaded ${foldersResult.data.length} folders from database`);
          setProjects(foldersResult.data); // Keep using 'projects' state name for UI compatibility
        } else {
          console.warn('⚠️ No folders data returned from getUserFolders');
        }

        // Load pinned moments separately (optional feature)
        try {
          const momentsResult = await chatService.getUserPinnedMoments(user.id);
          if (momentsResult?.data) {
            console.log(`✅ Loaded ${momentsResult.data.length} pinned moments from database`);
            setPinnedMoments(momentsResult.data);
          } else {
            setPinnedMoments([]);
          }
        } catch (momentsError) {
          console.warn('⚠️ Error loading pinned moments (non-critical):', momentsError);
          setPinnedMoments([]);
        }
      } catch (error) {
        console.error('Error loading chat data:', error);
      }
    };

    loadChatData();
  }, [user]);

  // Track previous URL conversation ID to detect navigation changes
  const prevUrlConversationIdRef = useRef(null);
  
  // CRITICAL: Clear stale chips on mount - ALWAYS run this first
  useEffect(() => {
    // ALWAYS clear chips when component mounts - no exceptions
    const storeState = useAskSwirlStore.getState();
    const chipCount = storeState.chips.length;
    console.log('🧹 [AIChatPageReal] Mount cleanup - checking chips:', chipCount);
    // ALWAYS clear, even if count is 0, to ensure clean state
    useAskSwirlStore.setState({ chips: [] });
    console.log('🧹 [AIChatPageReal] Forced chip clear on mount');
    // Double-check after a brief delay
    setTimeout(() => {
      const afterClear = useAskSwirlStore.getState().chips;
      if (afterClear.length > 0) {
        console.warn('⚠️ [AIChatPageReal] Chips still exist after clear, forcing again:', afterClear.length, afterClear.map(c => c.id));
        useAskSwirlStore.setState({ chips: [] });
      } else {
        console.log('✅ [AIChatPageReal] Chips successfully cleared on mount');
      }
    }, 50);
  }, []); // Run ONLY on mount

  // CRITICAL: Clear stale chips when switching chats
  useEffect(() => {
    // ALWAYS clear chips when conversation ID changes, even if count is 0
    const storeState = useAskSwirlStore.getState();
    const chipCount = storeState.chips.length;
    console.log('🧹 [AIChatPageReal] Clearing chips on chat switch (conversationId:', urlConversationId || 'none', '), current chips:', chipCount);
    // Force clear immediately with direct state manipulation
    useAskSwirlStore.setState({ chips: [] });
    // Verify after a brief delay
    setTimeout(() => {
      const afterClear = useAskSwirlStore.getState().chips;
      if (afterClear.length > 0) {
        console.warn('⚠️ [AIChatPageReal] Chips still exist after chat switch clear, forcing again:', afterClear.length, afterClear.map(c => c.id));
        useAskSwirlStore.setState({ chips: [] });
      } else {
        console.log('✅ [AIChatPageReal] Chips successfully cleared on chat switch');
      }
    }, 50);
  }, [urlConversationId]); // Clear when URL changes (switching chats)

  // Handle createDocFromFocusId query param (and legacy createPlanFromFocusId)
  useEffect(() => {
    const handleCreateDocFromFocus = async () => {
      if (!createDocFromFocusId || !user) return;

      try {
        // Dedupe guard: Check if doc created in last 60 seconds
        const { data: recentDocs } = await supabase
          .schema('public')
          .from('documents')
          .select('id, title, created_at')
          .eq('user_id', user.id)
          .eq('type', 'doc')
          .eq('source_focus_id', createDocFromFocusId)
          .gte('created_at', new Date(Date.now() - 60000).toISOString())
          .order('created_at', { ascending: false })
          .limit(1);

        if (recentDocs && recentDocs.length > 0) {
          // Reuse existing doc
          console.log('Reusing recent doc:', recentDocs[0].id);
          setInitialDocumentId(recentDocs[0].id);
          setShowDocsSidebar(true);
          // Remove both query params
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('createDocFromFocusId');
          newParams.delete('createPlanFromFocusId');
          navigate({ search: newParams.toString() }, { replace: true });
          return;
        }

        // Fetch focus
        const { data: focus, error: focusError } = await focusService.getFocusById(createDocFromFocusId);
        
        if (focusError || !focus) {
          console.error('Focus not found:', focusError);
          setError('Could not find focus');
          return;
        }

        // Create doc using the new helper method
        const { data: newDoc, error: docError } = await documentService.createDocFromFocus(user.id, focus);
        
        if (docError || !newDoc) {
          console.error('Failed to create doc:', docError);
          setError('Could not create doc');
          return;
        }

        // Open sidebar with new doc
        setInitialDocumentId(newDoc.id);
        setShowDocsSidebar(true);

        // Remove both query params to prevent duplicate on refresh
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('createDocFromFocusId');
        newParams.delete('createPlanFromFocusId');
        navigate({ search: newParams.toString() }, { replace: true });

      } catch (err) {
        console.error('Error handling createDocFromFocusId:', err);
        setError(err.message || 'Failed to create doc');
      }
    };

    handleCreateDocFromFocus();
  }, [createDocFromFocusId, user, navigate, searchParams]);

  // Handle openDocumentId query param
  useEffect(() => {
    if (openDocumentId && user) {
      console.log('[AIChatPageReal] Opening document from URL:', openDocumentId);
      setInitialDocumentId(openDocumentId);
      setShowDocsSidebar(true);
      
      // Remove query param after opening
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('openDocumentId');
      navigate({ search: newParams.toString() }, { replace: true });
    }
  }, [openDocumentId, user, navigate, searchParams]);

  // Dev logging for doc readiness
  useEffect(() => {
    if (DEV_DOC_READINESS_LOG && messages.length > 0) {
      const ready = isDocReady(messages);
      console.log(`[Doc Readiness] ${ready ? '✅ READY' : '⏳ Not ready'} - ${messages.length} messages`);
    }
  }, [messages]);

  // Load chat from URL if conversationId is present
  useEffect(() => {
    const loadChatFromUrl = async () => {
      if (!user) return;
      
      // Only proceed if URL conversation ID actually changed
      if (prevUrlConversationIdRef.current === urlConversationId) {
        return;
      }
      prevUrlConversationIdRef.current = urlConversationId;
      
      if (!urlConversationId) {
        // If no conversationId in URL, clean up empty chat if we're navigating away
        const currentChatIdValue = currentChatId;
        const messagesValue = messages;
        const chatsValue = chats;
        
        if (currentChatIdValue && messagesValue.length === 0) {
          const currentChat = chatsValue.find(c => c.id === currentChatIdValue);
          if (currentChat && (currentChat.title === 'New Chat' || !currentChat.title)) {
            // Delete the empty chat before clearing state
            try {
              await chatService.deleteChat(user.id, currentChatIdValue);
              // Refresh chats list
              const chatsResult = await chatService.getUserChats(user.id);
              if (chatsResult.data) {
                setChats(chatsResult.data);
              }
            } catch (error) {
              console.error('Error deleting empty chat:', error);
            }
          }
        }
        // Clear current chat state
        setCurrentChatId(null);
        setMessages([]);
        setTitleGenerated(false);
        return;
      }

      // Only load if URL conversation ID is different from current
      if (urlConversationId !== currentChatId) {
        await loadChat(urlConversationId);
      }
    };

    loadChatFromUrl();
  }, [user?.id, urlConversationId]); // Only depend on user.id and urlConversationId

  // Auto-select specific content if coming from detail modal
  useEffect(() => {
    if (contentId) {
      setSelectedSources([`file-${contentId}`]);
      setMessages([]);
    }
  }, [contentId]);

  // Handle prefilled prompt from URL (e.g., from Smart Insights)
  useEffect(() => {
    if (prefillPrompt && !initialPromptSent && user) {
      // Wait a brief moment for the chat to initialize
      const timer = setTimeout(() => {
        console.log('[AIChatPage] Sending prefilled prompt:', prefillPrompt);
        handleSendMessage({
          prompt: decodeURIComponent(prefillPrompt),
          context: [],
          chipData: []
        });
        setInitialPromptSent(true);
        // Clear the prompt from URL to prevent re-sending on refresh
        navigate('/ai-chat', { replace: true });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [prefillPrompt, initialPromptSent, user]);

  // Load minimal context from URL params (Smart Insights context)
  useEffect(() => {
    const loadSmartInsightsContext = async () => {
      if (!user?.id) return;
      
      try {
        // Load focus if focusId provided
        if (focusId) {
          console.log('[AIChatPage] Loading focus context:', focusId);
          const focuses = await focusService.getUserFocuses(user.id);
          const focus = focuses.find(f => f.id === focusId);
          if (focus) {
            setContextFocus(focus);
            console.log('✅ Loaded focus context:', focus.title);
          } else {
            console.warn('⚠️ Focus not found:', focusId);
          }
        }

        // Count referenced items if itemIds provided
        if (itemIds) {
          const ids = itemIds.split(',').filter(Boolean);
          setContextItemsCount(ids.length);
          console.log(`✅ Context: ${ids.length} related items`);
        }
      } catch (error) {
        console.error('Error loading Smart Insights context:', error);
      }
    };

    loadSmartInsightsContext();
  }, [focusId, itemIds, user?.id]);

  // Load context content based on selected sources with enhanced folder analysis
  useEffect(() => {
    const loadContextContent = async () => {
      if (!user?.id || !selectedSources.length) {
        setContextContent([]);
        return;
      }

      try {
        console.log('🔄 Loading context content for sources:', selectedSources);
        
        // If "All" is selected, load all content
        if (selectedSources.includes('All')) {
          const { data: allContent, error } = await contentService.getRecentContent(user.id, 50);
          if (error) {
            console.error('Error loading all content:', error);
            setContextContent([]);
          } else {
            setContextContent(allContent || []);
          }
          return;
        }

        const contextData = [];
        
        for (const sourceId of selectedSources) {
          try {
            // Check if it's a file (starts with 'file-')
            if (sourceId.startsWith('file-')) {
              const actualFileId = sourceId.replace('file-', '');
              const { data: fileContent, error: fileError } = await contentService.getContentById(actualFileId);
              if (!fileError && fileContent) {
                contextData.push({
                  type: 'file',
                  id: fileContent.id,
                  title: fileContent.title,
                  summary: fileContent.ai_analysis?.summary,
                  key_points: fileContent.key_points,
                  tags: fileContent.tags,
                  channel: fileContent.channel,
                  content_type: fileContent.content_type,
                  transcription: fileContent.transcription
                });
              }
            }
            // Check if it's a collection (starts with 'collection-')
            else if (sourceId.startsWith('collection-')) {
              const collectionId = sourceId.replace('collection-', '');
              const { data: collectionContent, error: collectionError } = await contentService.getContentByCollection(user.id, collectionId);
              if (!collectionError && collectionContent) {
                contextData.push(...collectionContent.map(item => ({
                  type: 'collection_item',
                  id: item.id,
                  title: item.title,
                  summary: item.ai_analysis?.summary,
                  key_points: item.key_points,
                  tags: item.tags,
                  channel: item.channel,
                  content_type: item.content_type,
                  transcription: item.transcription
                })));
              }
            }
            // Check if it's a channel (starts with 'channel-')
            else if (sourceId.startsWith('channel-')) {
              const channel = sourceId.replace('channel-', '');
              const { data: channelContent, error: channelError } = await contentService.getContentByChannel(user.id, channel);
              if (!channelError && channelContent) {
                contextData.push(...channelContent.map(item => ({
                  type: 'channel_item',
                  id: item.id,
                  title: item.title,
                  summary: item.ai_analysis?.summary,
                  key_points: item.key_points,
                  tags: item.tags,
                  channel: item.channel,
                  content_type: item.content_type,
                  transcription: item.transcription
                })));
              }
            }
          } catch (error) {
            console.error(`Error loading content for source ${sourceId}:`, error);
          }
        }
        
        setContextContent(contextData);
        console.log('✅ Loaded context content:', contextData.length, 'items');
      } catch (error) {
        console.error('Failed to load context content:', error);
        setContextContent([]);
      }
    };

    loadContextContent();
  }, [user?.id, selectedSources]);

  const creatingChatRef = useRef(false);
  
  const handleNewChat = async () => {
    // Prevent multiple simultaneous calls
    // Check loading state for current chat (if any)
    const currentLoading = currentChatId ? (loadingStates.get(currentChatId) || false) : false;
    if (creatingChatRef.current || currentLoading) return;
    
    creatingChatRef.current = true;
    
    try {
      // Clean up empty chat if navigating away from one
      if (currentChatId && messages.length === 0) {
        // Check if this is an empty "New Chat" that should be deleted
        const currentChat = chats.find(c => c.id === currentChatId);
        if (currentChat && (currentChat.title === 'New Chat' || !currentChat.title)) {
          // Delete the empty chat
          await chatService.deleteChat(user.id, currentChatId);
          // Refresh chats list to remove it
          const chatsResult = await chatService.getUserChats(user.id);
          if (chatsResult.data) {
            setChats(chatsResult.data);
          }
        }
      }

      // Save current chat if it has meaningful content
      if (messages.length > 0 && currentChatId) {
        await saveCurrentChat();
      }

      // Don't create new chat immediately - wait until user sends first message
      // Just reset state and navigate to base chat route
      
      // Clear loading state for previous chat (if any)
      if (currentChatId) {
        setIsLoading(currentChatId, false);
      }
      // Also clear 'pending' state
      setIsLoading('pending', false);
      
      setCurrentChatId(null);
      setTitleGenerated(false);
      titleGeneratedRef.current = false;
      
      // Navigate to base chat route (no ID)
      navigate('/ai-chat');

      // Reset state
      setMessages([]);
      // DEMO: Uncomment the line below to test plan message feature with sample data
      // setMessages(getSamplePlanConversation());
      setError(null);
      setSelectedSources(['All']); // Reset source selection
      setContextContent([]); // Clear context content
      clearChips(); // Clear chips from store
    } finally {
      creatingChatRef.current = false;
    }
  };

  // Save current chat to database with specific messages (to avoid stale state)
  const saveCurrentChatWithMessages = async (messagesToSave) => {
    if (!user || !currentChatId) {
      console.log('⚠️ [saveCurrentChatWithMessages] Skipping save - no user or currentChatId');
      return;
    }

    if (!messagesToSave || messagesToSave.length === 0) {
      console.log('⚠️ [saveCurrentChatWithMessages] Skipping save - no messages to save');
      return;
    }

    try {
      console.log(`🔍 [saveCurrentChatWithMessages] Saving chat ${currentChatId} with ${messagesToSave.length} messages`);
      console.log(`🔍 [saveCurrentChatWithMessages] Messages attachments:`, messagesToSave.map(m => ({
        type: m.type,
        id: m.id,
        attachmentsCount: m.attachments?.length || 0,
        attachments: m.attachments
      })));
      
      // Use existing title from chats list, or fallback to simple generation
      const existingChat = chats.find(c => c.id === currentChatId);
      const chatTitle = existingChat?.title || chatService.generateChatTitle(messagesToSave);

      const chatData = {
        id: currentChatId,
        title: chatTitle,
        messages: messagesToSave, // Use passed messages, not state
        folder_id: null, // Can be updated later when moving to folder
        focus_id: null, // Can be updated if chat is about a Focus
        sources: selectedSources
      };

      console.log(`💾 [saveCurrentChatWithMessages] Saving with title: "${chatTitle}"`);
      const result = await chatService.saveChat(user.id, chatData);
      
      if (result.data) {
        const savedChatId = result.data.id;
        console.log(`✅ [saveCurrentChatWithMessages] Saved chat ${savedChatId} successfully`);
        setCurrentChatId(savedChatId);
        
        // Navigate to the chat URL if not already there
        if (urlConversationId !== savedChatId) {
          navigate(`/ai-chat/${savedChatId}`);
        }
        
        // Refresh chats list
        const chatsResult = await chatService.getUserChats(user.id);
        if (chatsResult.data) {
          console.log(`✅ [saveCurrentChatWithMessages] Refreshed chats list: ${chatsResult.data.length} chats`);
          setChats(chatsResult.data);
        }
      } else {
        console.error('❌ [saveCurrentChatWithMessages] No data returned from saveChat');
      }
    } catch (error) {
      console.error('❌ [saveCurrentChatWithMessages] Error saving chat:', error);
    }
  };

  // Save current chat to database (uses current messages state)
  const saveCurrentChat = async () => {
    return saveCurrentChatWithMessages(messages);
  };

  // Create a doc from the current chat conversation
  const handleCreateDocFromChat = async () => {
    if (!currentChatId || !user || messages.length < 2) {
      return;
    }

    setIsCreatingDoc(true);

    try {
      // Fetch conversation details if needed (focus_id)
      const { data: conversation } = await supabase
        .schema('public')
        .from('conversations')
        .select('id, title, focus_id')
        .eq('id', currentChatId)
        .single();

      if (!conversation) {
        throw new Error('Conversation not found');
      }

      // Create doc with starter outline
      const { data: newDoc, error: docError} = await documentService.createDocFromConversation(
        user.id,
        conversation,
        conversation.title || 'Chat'
      );

      if (docError || !newDoc) {
        throw docError || new Error('Failed to create doc');
      }

      // Open sidebar immediately with starter outline
      setInitialDocumentId(newDoc.id);
      setShowDocsSidebar(true);

      // Generate AI content in background
      const { data: aiContent, error: aiError } = await documentService.generateDocContent(
        conversation,
        messages
      );

      if (!aiError && aiContent) {
        // Convert to TipTap JSON and update document
        const generatedContent = documentService.convertAIResponseToTipTapJSON(
          aiContent,
          conversation.id,
          conversation.title || 'Chat'
        );

        await documentService.updateDocument(newDoc.id, {
          content_json: generatedContent
        });
      } else {
        console.warn('AI generation failed, keeping starter outline:', aiError);
        // Non-blocking: user sees starter outline
      }

    } catch (err) {
      console.error('Error creating doc from chat:', err);
      setError(err.message || 'Failed to create doc');
    } finally {
      setIsCreatingDoc(false);
    }
  };

  // Reference card handlers
  const handleViewReference = (source) => {
    setPreviewSource(source);
  };

  const handleAddReference = async (sourceId, sourceType, sourceContent) => {
    // If doc is open, add immediately
    if (initialDocumentId) {
      await insertReferenceIntoDoc(initialDocumentId, sourceId, sourceType, sourceContent);
      return;
    }

    // Otherwise, show choose doc modal
    setPendingAddContent({ sourceId, sourceType, sourceContent });
    setShowChooseDocModal(true);
  };

  const insertReferenceIntoDoc = async (targetDocId, sourceId, sourceType, sourceContent) => {
    try {
      // Fetch current doc
      const { data: doc } = await documentService.getDocument(targetDocId);
      if (!doc) return;

      // Extract preview text
      const previewText = sourceType === 'doc' 
        ? extractPlainTextFromTipTap(sourceContent, 300)
        : (sourceContent || 'No preview available');

      // Build new content section
      const newSection = [
        {
          type: 'heading',
          attrs: { level: 2 },
          content: [{ type: 'text', text: `Added from ${sourceType}` }]
        },
        {
          type: 'paragraph',
          content: [{ type: 'text', text: previewText }]
        },
        {
          type: 'paragraph',
          content: [
            { type: 'text', text: 'Source: ' },
            { type: 'text', text: `${sourceType} ${sourceId}`, marks: [{ type: 'code' }] }
          ]
        }
      ];

      // Append to existing content
      const updatedContent = {
        ...doc.content_json,
        content: [...doc.content_json.content, ...newSection]
      };

      // Save
      await documentService.updateDocument(targetDocId, {
        content_json: updatedContent
      });

      // Open sidebar if not already open
      if (!showDocsSidebar) {
        setInitialDocumentId(targetDocId);
        setShowDocsSidebar(true);
      }
    } catch (error) {
      console.error('Error inserting reference:', error);
    }
  };

  const handleDocSelected = async (docId) => {
    if (!pendingAddContent) return;

    await insertReferenceIntoDoc(
      docId,
      pendingAddContent.sourceId,
      pendingAddContent.sourceType,
      pendingAddContent.sourceContent
    );

    setShowChooseDocModal(false);
    setPendingAddContent(null);
  };

  const handleSendMessage = async (payload) => {
    const { prompt, context = [], chipData = [], focusId = null } = payload;
    if (!prompt && context.length === 0) {
      return;
    }
    // Check loading state for current chat (if any)
    const currentLoading = currentChatId ? (loadingStates.get(currentChatId) || false) : false;
    if (currentLoading) {
      return;
    }

    // CRITICAL: Clear chips IMMEDIATELY to make CardStack disappear
    // ChatInputBar should have cleared them, but ensure they're gone
    if (chips.length > 0) {
      console.log('🧹 [handleSendMessage] Safety check: clearing remaining chips. Current chips:', chips.length);
      clearChips();
      // Force a small delay to ensure state updates propagate
      await new Promise(resolve => setTimeout(resolve, 10));
    }

    // Auto-create a new chat if we don't have one yet
    let chatId = currentChatId;
    if (!chatId && user) {
      console.log('🔍 [handleSendMessage] No current chat, creating new conversation...');
      
      // focusId is already destructured from payload at line 807
      if (focusId) {
        console.log(`🎯 [handleSendMessage] Creating chat with Focus context: ${focusId}`);
      }
      
      const newChatResult = await chatService.createNewChat(user.id, null, null, focusId);
      if (newChatResult.data) {
        chatId = newChatResult.data.id;
        setCurrentChatId(chatId);
        setTitleGenerated(false); // Reset flag so AI title will be generated on first save
        // Navigate to the new chat URL
        navigate(`/ai-chat/${chatId}`);
        // Refresh chats list to show new chat in sidebar
        const chatsResult = await chatService.getUserChats(user.id);
        if (chatsResult.data) {
          setChats(chatsResult.data);
        }
      } else {
        console.error('❌ [handleSendMessage] Failed to create new chat:', newChatResult.error);
      }
    }

    // Build attachments from chipData (full chip info passed from ChatInputBar)
    const attachments = chipData.filter(chip => chip.type === 'item').map(chip => {
      // Get full file URL - use file_url if available, otherwise try to construct from thumbnail
      let fileUrl = chip?.file_url || chip?.url || null;
      
      // If file_url is not a full URL, try to get it from storage
      if (fileUrl && !fileUrl.startsWith('http') && !fileUrl.startsWith('data:')) {
        try {
          if (fileUrl.includes('/') || fileUrl.startsWith('uploads/')) {
            const path = fileUrl.startsWith('uploads/') ? fileUrl.replace('uploads/', '') : fileUrl;
            const { data } = supabase.storage.from('user_uploads').getPublicUrl(path);
            fileUrl = data.publicUrl;
          }
        } catch (error) {
          console.warn('Failed to convert file URL to public URL:', error);
          fileUrl = null;
        }
      }
      
      // Get thumbnail URL
      let thumbnailUrl = chip?.thumbnail;
      if (thumbnailUrl && !thumbnailUrl.startsWith('http') && !thumbnailUrl.startsWith('data:') && !thumbnailUrl.startsWith('linear-gradient')) {
        try {
          if (thumbnailUrl.includes('/') || thumbnailUrl.startsWith('uploads/')) {
            const path = thumbnailUrl.startsWith('uploads/') ? thumbnailUrl.replace('uploads/', '') : thumbnailUrl;
            const { data } = supabase.storage.from('user_uploads').getPublicUrl(path);
            thumbnailUrl = data.publicUrl;
          }
        } catch (error) {
          console.warn('Failed to convert thumbnail to public URL:', error);
          thumbnailUrl = null;
        }
      }
      
      return {
        id: chip.id,
        file_url: fileUrl, // Full public Supabase Storage URL
        file_name: chip?.file_name || chip?.label || 'Attachment',
        file_type: chip?.file_type || 'unknown',
        file_size: chip?.file_size || null,
        thumbnail: thumbnailUrl || fileUrl || null, // Use file_url as fallback for thumbnail
        label: chip?.label || chip?.file_name || 'Attachment',
      };
    });
    
    console.log('📎 [handleSendMessage] Building user message with attachments:', {
      chipDataCount: chipData.length,
      attachmentsCount: attachments.length,
      attachments: attachments.map(a => ({ id: a.id, file_name: a.file_name, hasThumbnail: !!a.thumbnail, hasFileUrl: !!a.file_url }))
    });
    
    const userMessage = {
      id: uuidv4(),
      type: 'user',
      content: prompt || 'Using context only',
      timestamp: new Date(),
      attachments: attachments, // Always an array, even if empty
    };
    
    // Test A: Validate UUID format
    const isValidUUID = typeof userMessage.id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(userMessage.id);
    if (!isValidUUID) {
      console.error('❌ [TEST A FAILED] User message ID is not a valid UUIDv4:', userMessage.id, typeof userMessage.id);
    } else {
      console.log('✅ [TEST A PASSED] User message has valid UUIDv4:', userMessage.id);
    }
    
    console.log('📎 [handleSendMessage] User message created:', {
      id: userMessage.id,
      idType: typeof userMessage.id,
      isValidUUID,
      content: userMessage.content.substring(0, 50),
      attachmentsCount: userMessage.attachments.length,
      attachments: userMessage.attachments
    });
    
    setMessages(prev => {
      const updated = [...prev, userMessage];
      console.log('📎 [handleSendMessage] Messages state updated. Total messages:', updated.length, 'User message attachments:', userMessage.attachments.length);
      return updated;
    });
    // Set loading state for the current conversation
    // Use chatId (which may have been just created) or currentChatId
    const targetChatId = chatId || currentChatId;
    if (targetChatId) {
      setIsLoading(targetChatId, true);
    }
    setError(null);

    try {
      // Log context for debugging
      console.log('[Chat] Sending message with context:', {
        prompt: prompt || '',
        contextCount: context.length,
        context: context.map(c => ({ id: c.id, type: c.type }))
      });
      
      // Use the new Ask Swirl API with userId and conversationId for caching
      const response = await askSwirl({
        prompt: prompt || '',
        context: context,
      }, user?.id, chatId || currentChatId);
      
      console.log('[Chat] Model response received:', {
        hasContent: !!response.data?.content,
        referencedItemsCount: response.data?.referencedItems?.length || 0
      });

      if (response.error) {
        throw new Error(response.error);
      }

      const aiResponse = {
        id: uuidv4(),
        type: 'ai',
        content: response.data.content,
        referencedItems: response.data.referencedItems || [],
        summary: response.data.summary || null,
        followUps: response.data.followUps || [],
        timestamp: new Date(),
      };
      
      // Test B: Validate AI response UUID format
      const isValidAIUUID = typeof aiResponse.id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(aiResponse.id);
      if (!isValidAIUUID) {
        console.error('❌ [TEST B FAILED] AI response ID is not a valid UUIDv4:', aiResponse.id, typeof aiResponse.id);
      } else {
        console.log('✅ [TEST B PASSED] AI response has valid UUIDv4:', aiResponse.id);
      }
      
      console.log('📎 [handleSendMessage] AI response created:', {
        id: aiResponse.id,
        idType: typeof aiResponse.id,
        isValidUUID: isValidAIUUID,
        contentLength: aiResponse.content?.length || 0,
        referencedItemsCount: aiResponse.referencedItems?.length || 0
      });
      
      // Update messages state - use functional update to ensure we have the latest state
      let finalMessages = [];
      setMessages(prev => {
        // Find the userMessage we just added (it should be the last one)
        const lastMessage = prev[prev.length - 1];
        const userMsg = lastMessage && lastMessage.type === 'user' && lastMessage.id === userMessage.id 
          ? lastMessage 
          : userMessage;
        
        // Build final messages array - remove any duplicate userMessage and add both messages
        const updated = [...prev.filter(m => m.id !== userMessage.id), userMsg, aiResponse];
        finalMessages = updated; // Store for saving
        
        console.log('📎 [handleSendMessage] Final messages state:', {
          totalMessages: updated.length,
          userMessageAttachments: userMsg.attachments?.length || 0,
          userMessageAttachmentsDetail: userMsg.attachments,
          allAttachments: updated.filter(m => m.attachments && m.attachments.length > 0).map(m => ({
            type: m.type,
            id: m.id,
            count: m.attachments.length,
            attachments: m.attachments
          }))
        });
        return updated;
      });
      
      // Use finalMessages for saving (captured from setState callback)
      const updatedMessages = finalMessages.length > 0 ? finalMessages : [...messages.filter(m => m.id !== userMessage.id), userMessage, aiResponse];
      
      // Auto-save chat after AI response (only if we have a chatId)
      if (chatId) {
        // Generate title immediately if this is the first exchange (1 user message + 1 AI response = 2 total)
        if (updatedMessages.length === 2 && !titleGeneratedRef.current) {
          titleGeneratedRef.current = true; // Prevent duplicate generation
          console.log('🔍 [handleSendMessage] First exchange, generating AI title immediately...');
          
          // Generate and save title, then save the chat with all messages
          chatService.generateAIChatTitle(userMessage.content).then(async (aiTitle) => {
            console.log(`✅ [handleSendMessage] Generated title: "${aiTitle}"`);
            
            // Update title in database
            await chatService.renameChat(user.id, chatId, aiTitle);
            setTitleGenerated(true);
            
            // Save the chat with the new title and all messages
            // CRITICAL: Use updatedMessages which has attachments, not messages state
            console.log(`💾 [handleSendMessage] Saving chat with ${updatedMessages.length} messages and title: "${aiTitle}"`);
            console.log(`💾 [handleSendMessage] Messages to save:`, updatedMessages.map(m => ({
              type: m.type,
              id: m.id,
              attachmentsCount: m.attachments?.length || 0,
              attachments: m.attachments
            })));
            
            const chatData = {
              id: chatId,
              title: aiTitle,
              messages: updatedMessages, // Use updatedMessages with attachments
              folder_id: null,
              sources: selectedSources
            };
            
            const saveResult = await chatService.saveChat(user.id, chatData);
            
            if (saveResult.data) {
              console.log(`✅ [handleSendMessage] Chat saved successfully`);
              
              // Refresh chats list to show new title with animation
              const chatsResult = await chatService.getUserChats(user.id);
              if (chatsResult.data) {
                setChats(chatsResult.data);
              }
            }
          }).catch(err => {
            console.error('Error generating/updating chat title:', err);
            titleGeneratedRef.current = false; // Reset on error
            // Still save the chat even if title generation failed - use updatedMessages with attachments
            setTimeout(() => {
              saveCurrentChatWithMessages(updatedMessages);
            }, 500);
          });
        } else {
          // Not first exchange, just save normally after state updates
          // CRITICAL: Pass updatedMessages directly instead of relying on state
          setTimeout(() => {
            saveCurrentChatWithMessages(updatedMessages);
          }, 500);
        }
      }
    } catch (error) {
      console.error('AI Chat Error:', error);
      setError(error.message);
      
      const errorResponse = {
        id: uuidv4(),
        type: 'ai',
        content: `Sorry, I encountered an error: ${error.message}. Please check your API configuration and try again.`,
        timestamp: new Date(),
        isError: true
      };
      
      // Validate error response UUID
      const isValidErrorUUID = typeof errorResponse.id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(errorResponse.id);
      if (!isValidErrorUUID) {
        console.error('❌ [ERROR RESPONSE] Error response ID is not a valid UUIDv4:', errorResponse.id);
      }
      
      setMessages(prev => [...prev, errorResponse]);
    } finally {
      // Clear loading state for the conversation that was processing
      // Use chatId (which may have been just created) or currentChatId
      const targetChatId = chatId || currentChatId;
      if (targetChatId) {
        setIsLoading(targetChatId, false);
      }
    }
  };

  const [selectedContent, setSelectedContent] = useState(null);
  const [showContentModal, setShowContentModal] = useState(false);

  const handleItemClick = async (item) => {
    if (!user?.id || !item?.id) {
      console.warn('⚠️ [handleItemClick] Missing user or item ID:', { userId: user?.id, itemId: item?.id });
      return;
    }

    console.log('🔍 [handleItemClick] Clicked item:', item);

    try {
      // Try to fetch from deepsearch.items first
      const { data: deepsearchItems } = await deepsearchService.getItems(user.id);
      const deepsearchItem = deepsearchItems?.find(i => i.id === item.id);
      
      if (deepsearchItem) {
        console.log('✅ [handleItemClick] Found in deepsearch.items:', deepsearchItem.id);
        
        const mappedItem = {
          ...deepsearchItem,
          id: deepsearchItem.id,
          title: deepsearchItem.title || deepsearchItem.description || item.label || item.title || 'Untitled',
          description: deepsearchItem.description,
          url: deepsearchItem.url,
          content_type: deepsearchItem.source_type,
          thumbnail_url: deepsearchItem.meta?.thumbnail_url || deepsearchItem.thumbnail_url || item.thumbnail,
          analysis_data: deepsearchItem.meta?.analysis_data || {
            summary: deepsearchItem.meta?.analysis_data?.summary,
            ocr: {
              text: deepsearchItem.extracted_text,
              hasText: deepsearchItem.has_text,
              confidence: deepsearchItem.text_confidence,
            },
            colors: {
              primaryColor: deepsearchItem.primary_color,
              mood: deepsearchItem.color_mood,
              palette: deepsearchItem.color_palette,
            },
            tagging: {
              primaryTags: deepsearchItem.primary_tags,
              allTags: deepsearchItem.auto_tags,
            },
          },
          extracted_text: deepsearchItem.extracted_text,
          auto_tags: deepsearchItem.auto_tags,
          primary_tags: deepsearchItem.primary_tags,
          primary_color: deepsearchItem.primary_color,
          color_mood: deepsearchItem.color_mood,
          color_palette: deepsearchItem.color_palette,
          _source: 'deepsearch.items',
          source_type: deepsearchItem.source_type,
        };
        console.log('📦 [handleItemClick] Mapped item for modal:', mappedItem);
        setSelectedContent(mappedItem);
        setShowContentModal(true);
        return;
      }

      console.log('⚠️ [handleItemClick] Not found in deepsearch.items, trying instagram_content...');
      // Fallback to instagram_content
      const { data: contentData, error } = await contentService.getContentById(item.id);
      if (error || !contentData) {
        console.error('Error fetching content:', error);
        // Use item data as fallback
        setSelectedContent({
          ...item,
          id: item.id,
          title: item.label || 'Untitled',
        });
      } else {
        setSelectedContent(contentData);
      }
      setShowContentModal(true);
    } catch (error) {
      console.error('Error loading item details:', error);
      // Fallback: show item with available data
      setSelectedContent({
        ...item,
        id: item.id,
        title: item.label || 'Untitled',
      });
      setShowContentModal(true);
    }
  };

  // Handle moment selection (jump to specific message in conversation)
  const handleMomentSelect = async (conversationId, messageId) => {
    // Test F: Validate messageId is UUID when selecting moment
    const isValidUUID = typeof messageId === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(messageId);
    if (!isValidUUID) {
      console.error('❌ [TEST F FAILED] handleMomentSelect received invalid messageId:', {
        messageId,
        messageIdType: typeof messageId,
        isNumber: typeof messageId === 'number'
      });
    } else {
      console.log('✅ [TEST F] handleMomentSelect received valid UUID messageId:', messageId);
    }
    // Load the conversation
    await loadChat(conversationId);
    
    // Scroll to the specific message after messages are loaded
    setTimeout(() => {
      const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
      if (messageElement) {
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Highlight the message briefly with purple ring
        messageElement.classList.add('ring-2', 'ring-purple-400', 'ring-offset-2');
        setTimeout(() => {
          messageElement.classList.remove('ring-2', 'ring-purple-400', 'ring-offset-2');
        }, 3000);
      } else {
        // If message not found, try again after a longer delay
        console.warn('Message element not found, retrying...', messageId);
        setTimeout(() => {
          const retryElement = document.querySelector(`[data-message-id="${messageId}"]`);
          if (retryElement) {
            retryElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            retryElement.classList.add('ring-2', 'ring-purple-400', 'ring-offset-2');
            setTimeout(() => {
              retryElement.classList.remove('ring-2', 'ring-purple-400', 'ring-offset-2');
            }, 3000);
          }
        }, 1000);
      }
    }, 800);
  };

  // Chat management functions
  const loadChat = async (chatId) => {
    try {
      console.log('Loading chat:', chatId);
      
      // CRITICAL: Clear any existing chips before loading a chat
      const storeStateBefore = useAskSwirlStore.getState();
      if (storeStateBefore.chips.length > 0) {
        console.log('🧹 [loadChat] Clearing chips before loading chat:', storeStateBefore.chips.length, storeStateBefore.chips.map(c => c.id));
        useAskSwirlStore.setState({ chips: [] });
      }
      
      const result = await chatService.getChat(user.id, chatId);
      console.log('Chat data:', result.data);
      
      if (result.data) {
        // Check if this is an empty "New Chat" that should be deleted
        const messages = result.data.messages || [];
        const title = result.data.title || '';
        const isEmptyNewChat = messages.length === 0 && (title === 'New Chat' || !title);
        
        if (isEmptyNewChat) {
          // Delete the empty chat and navigate to base route
          console.log('Deleting empty "New Chat" conversation');
          await chatService.deleteChat(user.id, chatId);
          // Refresh chats list
          const chatsResult = await chatService.getUserChats(user.id);
          if (chatsResult.data) {
            setChats(chatsResult.data);
          }
          // Navigate to base route
          navigate('/ai-chat');
          setCurrentChatId(null);
          setMessages([]);
          setTitleGenerated(false);
          // Clear any stale chips when navigating away from chat
          const storeState = useAskSwirlStore.getState();
          if (storeState.chips.length > 0) {
            console.log('🧹 [loadChatFromUrl] Clearing chips when navigating away from empty chat');
            clearChips();
          }
          return;
        }
        
        // Clear loading state for previous chat (if any)
        if (currentChatId && currentChatId !== chatId) {
          setIsLoading(currentChatId, false);
        }
        
        setCurrentChatId(chatId);
        
        // Navigate to the chat URL if not already there
        if (urlConversationId !== chatId) {
          navigate(`/ai-chat/${chatId}`);
        }
        
        // Ensure new chat doesn't show loading state unless it's actually loading
        setIsLoading(chatId, false);
        
        console.log('Messages to load:', messages);
        
        // Validate message format and restore attachments from metadata
        const validMessages = messages.map((msg, index) => {
          // Restore attachments from multiple possible locations
          let attachments = null;
          if (msg.attachments && Array.isArray(msg.attachments)) {
            attachments = msg.attachments;
            console.log(`📎 [loadChat] Message ${index} has attachments in msg.attachments:`, attachments.length);
          } else if (msg.metadata?.attachments && Array.isArray(msg.metadata.attachments)) {
            attachments = msg.metadata.attachments;
            console.log(`📎 [loadChat] Message ${index} has attachments in msg.metadata.attachments:`, attachments.length);
          } else if (msg.attachments && !Array.isArray(msg.attachments)) {
            // If attachments exists but isn't an array, wrap it
            attachments = [msg.attachments];
            console.log(`📎 [loadChat] Message ${index} has non-array attachments, wrapping:`, attachments.length);
          } else if (msg.metadata?.attachments && !Array.isArray(msg.metadata.attachments)) {
            attachments = [msg.metadata.attachments];
            console.log(`📎 [loadChat] Message ${index} has non-array metadata.attachments, wrapping:`, attachments.length);
          } else {
            console.log(`📎 [loadChat] Message ${index} has NO attachments. msg.attachments:`, msg.attachments, 'msg.metadata:', msg.metadata);
          }
          
          // Always ensure attachments is an array (empty if null)
          const normalizedAttachments = Array.isArray(attachments) ? attachments : [];
          
          // CRITICAL: Restore referencedItems for AI messages
          const referencedItems = (msg.type === 'ai' || msg.role === 'ai') && Array.isArray(msg.metadata?.referencedItems)
            ? msg.metadata.referencedItems
            : (msg.referencedItems && Array.isArray(msg.referencedItems) ? msg.referencedItems : undefined);
          
          if (referencedItems && referencedItems.length > 0) {
            console.log(`📎 [loadChat] Message ${index} has referencedItems:`, referencedItems.length);
          }
          
          // Test D: Backward compatibility - Map numeric IDs to UUIDs in-memory only
          let messageId = msg.id;
          let legacyId = null;
          if (typeof msg.id === 'number') {
            legacyId = msg.id;
            messageId = uuidv4();
            console.log(`✅ [TEST D] Migrated legacy numeric ID ${legacyId} to UUID ${messageId} (in-memory only, NOT saved to DB)`);
            console.log(`📝 [TEST D] Legacy ID preserved in legacyId field, new UUID: ${messageId}`);
          } else if (!msg.id) {
            // Fallback if no ID at all
            messageId = uuidv4();
            console.log(`⚠️ [TEST D] Message ${index} had no ID, generated UUID: ${messageId}`);
          } else if (typeof msg.id === 'string') {
            // Validate existing string ID is UUID
            const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(msg.id);
            if (!isValidUUID) {
              console.warn(`⚠️ [TEST D] Message ${index} has non-UUID string ID: ${msg.id}, keeping as-is`);
            }
          }
          
          const restoredMessage = {
            id: messageId,
            ...(legacyId !== null && { legacyId }), // Store legacy ID for reference if migrated
            type: msg.type || 'user',
            content: msg.content || '',
            timestamp: msg.timestamp ? (msg.timestamp instanceof Date ? msg.timestamp : new Date(msg.timestamp)) : new Date(),
            attachments: normalizedAttachments, // Always an array
            referencedItems: referencedItems, // Restore referencedItems for AI messages
          };
          
          console.log(`📎 [loadChat] Restored message ${index}:`, {
            id: restoredMessage.id,
            type: restoredMessage.type,
            contentLength: restoredMessage.content.length,
            attachmentsCount: restoredMessage.attachments.length,
            referencedItemsCount: referencedItems?.length || 0,
            attachments: restoredMessage.attachments.map(a => ({ id: a.id, file_name: a.file_name, hasThumbnail: !!a.thumbnail })),
            referencedItems: referencedItems
          });
          
          return restoredMessage;
        });
        
        // Test F: Validate all messages have UUIDs after reload
        const allHaveUUIDs = validMessages.every(m => 
          typeof m.id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(m.id)
        );
        if (!allHaveUUIDs) {
          console.error('❌ [TEST F FAILED] Some messages do not have valid UUIDs after reload');
          validMessages.forEach((m, idx) => {
            const isValid = typeof m.id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(m.id);
            if (!isValid) {
              console.error(`  Message ${idx}:`, { id: m.id, type: typeof m.id, hasLegacyId: !!m.legacyId });
            }
          });
        } else {
          console.log('✅ [TEST F PASSED] All messages have valid UUIDs after reload');
        }
        
        console.log('✅ [loadChat] Restored messages with attachments:', validMessages.map(m => ({
          id: m.id,
          idType: typeof m.id,
          hasLegacyId: !!m.legacyId,
          type: m.type,
          hasAttachments: !!m.attachments && m.attachments.length > 0,
          attachmentCount: m.attachments?.length || 0,
          attachments: m.attachments
        })));
        
        // CRITICAL: Set messages into state - this ensures attachments are available in React state
        setMessages(validMessages);
        
        // Verify attachments are in state after setting
        console.log('✅ [loadChat] State updated. Messages in state:', validMessages.length);
        console.log('✅ [loadChat] Total attachments restored:', validMessages.reduce((sum, m) => sum + (m.attachments?.length || 0), 0));
        
        // Double-check: verify state was actually set
        setTimeout(() => {
          console.log('🔍 [loadChat] Verification: Checking if attachments are in React state...');
        }, 100);
        setSelectedSources(result.data.sources || ['All']);
        setError(null);
        
        // If chat has messages but title is still "New Chat", generate AI title
        const needsTitleUpdate = (result.data.title === 'New Chat' || !result.data.title) && validMessages.length > 0;
        if (needsTitleUpdate) {
          const firstUserMessage = validMessages.find(msg => msg.type === 'user');
          if (firstUserMessage) {
            console.log('🔍 [loadChat] Chat has messages but no title, generating AI title...');
            const aiTitle = await chatService.generateAIChatTitle(firstUserMessage.content);
            
            // Update the chat title in the database
            await chatService.renameChat(user.id, chatId, aiTitle);
            
            // Refresh chats list to show updated title
            const chatsResult = await chatService.getUserChats(user.id);
            if (chatsResult.data) {
              setChats(chatsResult.data);
            }
            
            setTitleGenerated(true);
          }
        } else if (result.data.title && result.data.title !== 'New Chat') {
          setTitleGenerated(true);
        } else {
          setTitleGenerated(false);
        }
        
        console.log('Chat loaded successfully');
      } else {
        console.error('No chat data received');
        setError('Failed to load chat');
      }
    } catch (error) {
      console.error('Error loading chat:', error);
      setError('Failed to load chat: ' + error.message);
    }
  };

  const deleteChat = async (chatId) => {
    try {
      await chatService.deleteChat(user.id, chatId);
      setChats(prev => prev.filter(chat => chat.id !== chatId));
      if (currentChatId === chatId) {
        handleNewChat();
      }
    } catch (error) {
      console.error('Error deleting chat:', error);
    }
  };

  const renameChat = async (chatId, newTitle) => {
    try {
      await chatService.renameChat(user.id, chatId, newTitle);
      setChats(prev => prev.map(chat => 
        chat.id === chatId ? { ...chat, title: newTitle } : chat
      ));
      setEditingChatId(null);
    } catch (error) {
      console.error('Error renaming chat:', error);
    }
  };

  const togglePinChat = async (chatId, isPinned) => {
    try {
      await chatService.togglePinChat(user.id, chatId, isPinned);
      setChats(prev => prev.map(chat => 
        chat.id === chatId ? { ...chat, is_pinned: isPinned } : chat
      ));
    } catch (error) {
      console.error('Error toggling pin chat:', error);
    }
  };

  const archiveChat = async (chatId) => {
    try {
      await chatService.archiveChat(user.id, chatId);
      setChats(prev => prev.filter(chat => chat.id !== chatId));
      if (currentChatId === chatId) {
        handleNewChat();
      }
    } catch (error) {
      console.error('Error archiving chat:', error);
    }
  };

  const moveChatToProject = async (chatId, folderId) => {
    try {
      console.log(`🔍 [moveChatToProject] Moving chat ${chatId} to folder ${folderId || 'null'}`);
      const result = await chatService.moveChatToFolder(user.id, chatId, folderId);
      
      if (result.error) {
        console.error('❌ [moveChatToProject] Error:', result.error);
        return;
      }
      
      console.log(`✅ [moveChatToProject] Successfully moved chat to folder`);
      
      // Refresh the entire chat list to ensure UI is updated
      const chatsResult = await chatService.getUserChats(user.id);
      if (chatsResult.data) {
        console.log(`✅ [moveChatToProject] Refreshed chats list: ${chatsResult.data.length} chats`);
        setChats(chatsResult.data);
      }
    } catch (error) {
      console.error('❌ [moveChatToProject] Error moving chat to folder:', error);
    }
  };

  // Folder expansion handler
  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  // Drag and drop handlers
  const handleDragStart = (e, chatId) => {
    console.log('Drag started for chat:', chatId);
    e.dataTransfer.setData('text/plain', chatId);
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
    e.target.style.transform = 'scale(1.05)';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    e.target.style.transform = 'scale(1)';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    e.currentTarget.style.backgroundColor = 'rgba(147, 51, 234, 0.15)';
    e.currentTarget.style.border = '2px dashed rgba(147, 51, 234, 0.5)';
  };

  const handleDragLeave = (e) => {
    e.currentTarget.style.backgroundColor = '';
    e.currentTarget.style.border = '';
  };

  const handleDrop = async (e, folderId) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = '';
    e.currentTarget.style.border = '';
    const chatId = e.dataTransfer.getData('text/plain');
    console.log('Drop event:', { chatId, folderId });
    if (chatId && folderId) {
      console.log('Moving chat to folder:', chatId, folderId);
      await moveChatToProject(chatId, folderId);
    }
  };

  const createProject = async (folderName) => {
    if (!folderName || !folderName.trim()) return;

    try {
      const result = await chatService.createFolder(user.id, {
        name: folderName.trim()
      });
      
      if (result.data) {
        setProjects(prev => [result.data, ...prev]);
        // Also refresh chats to get updated folder associations
        const chatsResult = await chatService.getUserChats(user.id);
        if (chatsResult.data) {
          setChats(chatsResult.data);
        }
      }
    } catch (error) {
      console.error('Error creating folder:', error);
    }
  };

  const renameProject = async (folderId, newName) => {
    try {
      await chatService.renameFolder(user.id, folderId, newName);
      setProjects(prev => prev.map(folder => 
        folder.id === folderId ? { ...folder, name: newName } : folder
      ));
      setEditingProjectId(null);
    } catch (error) {
      console.error('Error renaming folder:', error);
    }
  };

  const deleteProject = async (folderId) => {
    try {
      await chatService.deleteFolder(user.id, folderId);
      setProjects(prev => prev.filter(folder => folder.id !== folderId));
      // Also refresh chats to remove any associations with deleted folder
      const chatsResult = await chatService.getUserChats(user.id);
      if (chatsResult.data) {
        setChats(chatsResult.data);
      }
    } catch (error) {
      console.error('Error deleting folder:', error);
    }
  };

  // Filter chats based on search query
  const filteredChats = chats.filter(chat => 
    chat.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex h-screen bg-[#F5F3F0] relative">
      {/* Subtle background with very light purple tint */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#F5F3F0] via-[#F7F5F2] to-[#F5F3F0]"></div>
      {/* Subtle purple accent gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-purple-50/20 pointer-events-none"></div>

      {/* Left Sidebar */}
      <Sidebar
        isExpanded={isExpanded}
        onToggle={toggle}
        chats={chats.filter(chat => {
          // Filter out empty "New Chat" conversations
          // Keep chats that either have a real title or are the currently active chat
          return !(chat.title === 'New Chat' || !chat.title) || chat.id === currentChatId;
        })}
        currentChatId={currentChatId}
        onChatSelect={loadChat}
        onPinChat={togglePinChat}
        onRenameChat={renameChat}
        onMoveToProject={moveChatToProject}
        onArchiveChat={archiveChat}
        onDeleteChat={deleteChat}
        projects={projects}
        expandedFolders={expandedFolders}
        onToggleFolder={toggleFolder}
        onCreateProject={createProject}
        onRenameProject={renameProject}
        onDeleteProject={deleteProject}
        onSelectFolder={(projectId) => {
          // Filter chats by project when folder is selected
          // This could be implemented to show only chats in that project
          console.log('Selected folder:', projectId);
        }}
        onCreateNewChat={handleNewChat}
        pinnedMoments={pinnedMoments}
        onMomentSelect={handleMomentSelect}
        showSearchModal={showSearchModal}
        setShowSearchModal={setShowSearchModal}
      />

      {/* Arrow for minimized sidebar */}
      {!isExpanded && (
        <button
          onClick={toggle}
          className="fixed left-16 top-8 w-6 h-6 bg-white/90 backdrop-blur-sm rounded-r-lg flex items-center justify-center text-neutral-600 hover:bg-white hover:text-[#7A47F5] transition-all duration-500 ease-out z-[60] shadow-sm border border-neutral-200/60"
        >
          <ChevronRight size={16} />
        </button>
      )}

      {/* Main Content Area */}
      <main className={`flex-1 flex flex-row h-screen overflow-hidden transition-all duration-500 ease-out ${isExpanded ? 'ml-72' : 'ml-16'}`}>
        <div 
          style={{ transitionDuration: '800ms' }}
          className={`flex-1 flex flex-col min-h-0 transition-all ease-out ${showDocsSidebar ? 'mr-[420px]' : 'mr-0'}`}>
        {/* Chat Interface */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Header */}
          <div className="relative z-10 px-8 pt-8 pb-4">
            <div className="flex items-center justify-between">
              <h1 className="text-5xl font-serif text-neutral-900">
                Ask <span className="text-[#7A47F5]">Swirl</span>
              </h1>
              <button
                onClick={() => setShowDocsSidebar(!showDocsSidebar)}
                style={{
                  transition: 'all 800ms ease-out'
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
                  showDocsSidebar
                    ? 'bg-[#7A47F5] text-white border-[#7A47F5]'
                    : 'bg-white text-neutral-700 border-neutral-300 hover:border-[#7A47F5] hover:text-[#7A47F5]'
                }`}
              >
                <FileText size={18} />
                <span className="font-medium text-sm">Docs</span>
              </button>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50/80 border border-red-200/50 rounded-xl p-4 mx-8 mt-4 shadow-sm">
              <div className="flex items-center">
                <div className="text-red-700 text-sm">
                  <strong>API Error:</strong> {error}
                </div>
              </div>
            </div>
          )}

          {/* Messages Area or Empty State */}
          {messages.length === 0 && !isLoading ? (
            <AskSwirlEmptyState 
              userName={user?.user_metadata?.name || user?.email?.split('@')[0] || 'there'}
            />
          ) : (
            <div className="flex-1 overflow-y-auto px-8 py-6 pb-40 space-y-1 relative z-10 min-h-0">
              {messages.map((message, index) => {
                // Handle plan message type
                if (message.type === 'plan') {
                  return (
                    <PlanMessage
                      key={message.id}
                      plan={message.plan}
                      onSaveToSwirl={(plan) => {
                        console.log('Save to Swirl:', plan.id);
                        // TODO: Implement save to Swirl functionality
                        // This will be implemented when the Swirl section feature is ready
                      }}
                      onSaveToFocus={(plan) => {
                        console.log('Save to Focus:', plan.id);
                        // TODO: Implement save to Focus functionality
                        // 1. Check if user has an active focus
                        // 2. Show focus selector modal or create new focus
                        // 3. Save plan to selected focus section
                      }}
                      onDuplicateInNewChat={(plan) => {
                        console.log('Duplicate in new chat:', plan.id);
                        // TODO: Implement duplicate functionality
                        // 1. Create new chat conversation
                        // 2. Add this plan as first message
                        // 3. Navigate to new chat
                      }}
                    />
                  );
                }
                
                // Handle regular chat messages
                return (
                  <React.Fragment key={message.id || index}>
                    <EnhancedChatMessage
                      message={message}
                      onItemClick={handleItemClick}
                      conversationId={currentChatId}
                      onPinUpdate={async () => {
                        // Refresh pinned moments in sidebar
                        if (user) {
                          const momentsResult = await chatService.getUserPinnedMoments(user.id);
                          if (momentsResult.data) {
                            setPinnedMoments(momentsResult.data);
                          }
                        }
                      }}
                      onFollowUpClick={(followUpText) => {
                        handleSendMessage({
                          prompt: followUpText,
                          context: [],
                          chipData: []
                        });
                      }}
                    />
                    
                    {/* Render ReferenceCards after assistant messages */}
                    {message.role === 'assistant' && user && (
                      <ReferenceCards
                        userId={user.id}
                        focusId={focusId}
                        conversationFocusId={contextFocus?.id}
                        currentDocId={initialDocumentId}
                        onView={handleViewReference}
                        onAdd={handleAddReference}
                      />
                    )}
                  </React.Fragment>
                );
              })}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="max-w-[75%]">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#7A47F5] to-[#9D6FF5] shadow-sm p-1.5">
                        <svg 
                          viewBox="500 300 1100 1400" 
                          className="w-full h-full"
                          preserveAspectRatio="xMidYMid meet"
                        >
                          <path 
                            fill="white" 
                            d="M 1164.22 315.416 C 1226.57 311.925 1301.55 320.971 1352.42 359.073 C 1389.34 386.728 1415.89 420.703 1421.8 466.98 C 1429.24 525.221 1418.64 581.381 1382.07 628.153 C 1334.13 689.473 1262.07 738.867 1183.74 748.707 C 1145.35 754.215 1101.89 750.712 1069.86 726.381 C 1046.32 708.505 1026.99 663.003 1048.02 636.427 C 1052.28 631.046 1063.15 624.143 1070.51 624.061 C 1095.77 623.782 1088.83 661.424 1107.65 672.179 C 1124.04 683.691 1146.6 686.047 1165.23 683.95 C 1209.4 678.977 1249.12 651.074 1276.51 617.326 C 1306 581.987 1317.83 539.557 1312.81 493.817 C 1303.37 407.762 1206.47 377.994 1132.28 388.792 C 1035.81 402.834 941.899 455.397 880.433 530.823 C 830.256 592.395 813.55 679.242 865.397 744.693 C 871.503 752.547 878.103 760.229 885.227 767.201 C 955.139 835.617 1047.3 878.027 1131.31 926.345 C 1188.27 959.099 1244.86 989.759 1296.98 1030.25 C 1356.72 1076.65 1403.27 1137.51 1411.7 1214.95 C 1421.33 1300.6 1393.24 1378.11 1339.97 1444.28 C 1248.37 1558.07 1100.97 1622.81 958.279 1638.76 C 846.435 1648.92 726.537 1629.99 636.56 1559.29 C 534.114 1478.79 526.595 1333.69 608.073 1235.77 C 654.169 1180.37 718.444 1134.95 791.291 1125.04 C 811.442 1122.3 849.065 1122.78 851.429 1150.12 C 853.464 1178.04 815.603 1191.49 796.211 1205.52 C 782.773 1215.12 770.559 1226.33 759.838 1238.89 C 695.15 1313.3 678.282 1428.11 759.769 1496.07 C 814.706 1541.88 889.784 1555.81 959.625 1551.15 C 1047.74 1545.27 1135.06 1502.38 1193.72 1436.49 C 1235.22 1389.37 1265.61 1315.71 1260.02 1252.2 C 1250.14 1139.97 1152.01 1079.22 1063.53 1030.09 L 970.158 978.703 C 903.638 941.507 850.734 907.97 790.784 860.011 C 663.61 761.404 682.623 611.639 782.669 501.454 C 880.816 393.361 1018.27 325.179 1164.22 315.416 z"
                          />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="inline-block px-4 py-3 rounded-xl bg-neutral-50 text-neutral-800 border border-neutral-200/50">
                          <div className="flex items-center gap-2">
                            <div className="flex gap-1">
                              <div className="w-1.5 h-1.5 bg-[#7A47F5] rounded-full animate-bounce"></div>
                              <div className="w-1.5 h-1.5 bg-[#7A47F5] rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                              <div className="w-1.5 h-1.5 bg-[#7A47F5] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                            </div>
                            <span className="text-sm text-neutral-600">Generating response, please wait</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}

        {/* Smart Insights Context - Minimal Labels */}
        {(contextFocus || contextItemsCount > 0) && (
          <div className="px-8 pb-2">
            <div className="text-xs text-gray-500">
              {contextFocus && (
                <span>Context: Focus → <span className="font-medium text-gray-700">{contextFocus.title}</span></span>
              )}
              {contextFocus && contextItemsCount > 0 && <span> • </span>}
              {contextItemsCount > 0 && (
                <span>Context: <span className="font-medium text-gray-700">{contextItemsCount} related items</span></span>
              )}
            </div>
          </div>
        )}

        {/* Turn Chat into Doc Button */}
        {currentChatId && messages.length >= 2 && (
          <div className="flex justify-center py-4 px-8">
            <button
              onClick={handleCreateDocFromChat}
              disabled={isCreatingDoc}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreatingDoc ? (
                <>Creating doc...</>
              ) : (
                <>
                  <FileText size={16} />
                  Turn this chat into a doc
                </>
              )}
            </button>
          </div>
        )}

        {/* Enhanced Chat Input Bar */}
        <ChatInputBar onSend={handleSendMessage} isProcessing={isLoading} messages={messages} showDocsSidebar={showDocsSidebar} />

        </div>

        {/* Context Selector */}
        <SwirlContextSelector />

        {/* Upload Modal */}
        <UploadModal />

        {/* Content Detail Modal */}
        <ContentDetailModal
          isOpen={showContentModal}
          onClose={() => {
            setShowContentModal(false);
            setSelectedContent(null);
          }}
          content={selectedContent}
        />
        </div>

        {/* Documents Sidebar - slides in from right */}
        <div 
          style={{ transitionDuration: '800ms' }}
          className={`fixed top-0 right-0 h-screen transition-transform ease-out z-40 ${
            showDocsSidebar ? 'translate-x-0' : 'translate-x-full'
          }`}>
          <DocumentsSidebar
            onClose={() => setShowDocsSidebar(false)}
            initialDocumentId={initialDocumentId}
          />
        </div>
      </main>

      {/* New Folder Modal */}
      {showNewProjectModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/95 backdrop-blur-lg rounded-xl p-6 w-96 shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Create New Folder</h3>
            <input
              type="text"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              placeholder="Folder name"
              className="w-full p-3 bg-white/60 backdrop-blur-lg border border-white/50 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent mb-4"
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  createProject();
                }
              }}
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowNewProjectModal(false);
                  setNewProjectName('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createProject}
                disabled={!newProjectName.trim()}
                className="px-4 py-2 bg-gradient-to-br from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat Search Modal - Rendered at page level to overlay main canvas */}
      <ChatSearchModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        chats={chats}
        currentChatId={currentChatId}
        onChatSelect={loadChat}
        onCreateNewChat={handleNewChat}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      {/* Reference Preview Modal */}
      <ReferencePreviewModal
        source={previewSource}
        isOpen={!!previewSource}
        onClose={() => setPreviewSource(null)}
        onAdd={(sourceId, sourceType, sourceContent) => {
          handleAddReference(sourceId, sourceType, sourceContent);
          setPreviewSource(null);
        }}
      />

      {/* Choose Doc Modal */}
      <ChooseDocModal
        isOpen={showChooseDocModal}
        onClose={() => {
          setShowChooseDocModal(false);
          setPendingAddContent(null);
        }}
        onSelectDoc={handleDocSelected}
        userId={user?.id}
        onCreateNew={async (title) => {
          const { data: newDoc } = await documentService.createDoc(user.id, {
            title: title || 'Untitled',
            source_conversation_id: currentChatId,
            source_focus_id: focusId || contextFocus?.id
          });
          if (newDoc) {
            await handleDocSelected(newDoc.id);
          }
        }}
      />
    </div>
  );
}
